import { create } from "zustand";

import type { ConversationSuggestions } from "@/lib/suggestions/conversationSuggestionContract";
import {
  ensureConversationSuggestions,
  fetchConversationSuggestions,
  type SuggestionRecoveryResult,
} from "@/services/suggestionService";

export interface ConversationSuggestionScope {
  actorId: string;
  projectId: string;
  conversationId: string;
  headMessageId: string;
}

export interface SuggestionEntry {
  scope: ConversationSuggestionScope;
  suggestions?: ConversationSuggestions;
  etag?: string;
  loading: boolean;
  error: string | null;
  recoveryEpoch: number;
  /** Highest accepted revision for this exact actor and head, retained across a 204 tombstone. */
  revisionWatermark: number;
}

export interface SuggestionReplacementResult {
  entry: SuggestionEntry;
  applied: boolean;
}

function sameScope(
  left: ConversationSuggestionScope,
  right: ConversationSuggestionScope,
): boolean {
  return (
    left.actorId === right.actorId &&
    left.projectId === right.projectId &&
    left.conversationId === right.conversationId &&
    left.headMessageId === right.headMessageId
  );
}

function setMatchesScope(
  suggestions: ConversationSuggestions,
  scope: ConversationSuggestionScope,
): boolean {
  return (
    suggestions.actorId === scope.actorId &&
    suggestions.projectId === scope.projectId &&
    suggestions.conversationId === scope.conversationId &&
    suggestions.headMessageId === scope.headMessageId
  );
}

export function conversationSuggestionsEtag(
  suggestions: Pick<ConversationSuggestions, "id" | "revision">,
): string {
  return `"${suggestions.id}:${suggestions.revision}"`;
}

export function reduceSuggestionReplacement(
  entry: SuggestionEntry,
  suggestions: ConversationSuggestions,
  etag?: string,
): SuggestionReplacementResult {
  if (!setMatchesScope(suggestions, entry.scope)) {
    return { entry, applied: false };
  }
  const previousRevision = entry.revisionWatermark;
  if (suggestions.revision <= previousRevision) {
    return { entry, applied: false };
  }
  return {
    entry: {
      ...entry,
      suggestions,
      etag: etag ?? conversationSuggestionsEtag(suggestions),
      loading: false,
      error: null,
      revisionWatermark: suggestions.revision,
    },
    applied: true,
  };
}

type SuggestionLoader = (
  projectId: string,
  conversationId: string,
  headMessageId: string,
  etag?: string,
) => Promise<SuggestionRecoveryResult>;

interface ConversationSuggestionsStore {
  entriesByConversationId: Record<string, SuggestionEntry | undefined>;
  /** Monotonic guard for delayed requests across clear/logout and actor ABA changes. */
  nextRecoveryEpoch: number;
  prepareScope: (scope: ConversationSuggestionScope) => void;
  isCurrentScope: (scope: ConversationSuggestionScope) => boolean;
  applyReplacement: (
    actorId: string,
    suggestions: ConversationSuggestions,
    options?: { etag?: string },
  ) => boolean;
  ensure: (scope: ConversationSuggestionScope) => Promise<void>;
  recover: (scope: ConversationSuggestionScope) => Promise<void>;
  clearConversation: (conversationId: string) => void;
  clearAll: () => void;
}

export const useConversationSuggestionsStore = create<ConversationSuggestionsStore>(
  (set, get) => {
    const load = async (
      scope: ConversationSuggestionScope,
      loader: SuggestionLoader,
    ) => {
      get().prepareScope(scope);
      const current = get().entriesByConversationId[scope.conversationId];
      if (!current || !sameScope(current.scope, scope)) return;
      const epoch = get().nextRecoveryEpoch + 1;
      const requestedRevision = current.revisionWatermark;
      set((state) => ({
        nextRecoveryEpoch: epoch,
        entriesByConversationId: {
          ...state.entriesByConversationId,
          [scope.conversationId]: {
            ...current,
            loading: !current.suggestions,
            error: null,
            recoveryEpoch: epoch,
          },
        },
      }));

      try {
        const result = await loader(
          scope.projectId,
          scope.conversationId,
          scope.headMessageId,
          current.etag,
        );
        const latest = get().entriesByConversationId[scope.conversationId];
        if (
          !latest ||
          latest.recoveryEpoch !== epoch ||
          !sameScope(latest.scope, scope)
        ) {
          return;
        }
        if (result.status === "current") {
          const reduced = reduceSuggestionReplacement(
            latest,
            result.suggestions,
            result.etag,
          );
          set((state) => ({
            entriesByConversationId: {
              ...state.entriesByConversationId,
              [scope.conversationId]: reduced.applied
                ? reduced.entry
                : {
                    ...latest,
                    loading: false,
                    error: null,
                    etag:
                      setMatchesScope(result.suggestions, scope) &&
                      latest.revisionWatermark === result.suggestions.revision
                        ? result.etag ?? latest.etag
                        : latest.etag,
                  },
            },
          }));
          return;
        }
        if (result.status === "empty") {
          set((state) => ({
            entriesByConversationId: {
              ...state.entriesByConversationId,
              [scope.conversationId]: {
                ...latest,
                ...(latest.revisionWatermark === requestedRevision
                  ? { suggestions: undefined, etag: undefined }
                  : {}),
                loading: false,
                error: null,
              },
            },
          }));
          return;
        }
        set((state) => ({
          entriesByConversationId: {
            ...state.entriesByConversationId,
            [scope.conversationId]: {
              ...latest,
              etag:
                latest.revisionWatermark === requestedRevision
                  ? result.etag ?? latest.etag
                  : latest.etag,
              loading: false,
              error: null,
            },
          },
        }));
      } catch (error) {
        const latest = get().entriesByConversationId[scope.conversationId];
        if (
          !latest ||
          latest.recoveryEpoch !== epoch ||
          !sameScope(latest.scope, scope)
        ) {
          return;
        }
        set((state) => ({
          entriesByConversationId: {
            ...state.entriesByConversationId,
            [scope.conversationId]: {
              ...latest,
              loading: false,
              error:
                error instanceof Error
                  ? error.message
                  : "Failed to load suggestions",
            },
          },
        }));
      }
    };

    return {
      entriesByConversationId: {},
      nextRecoveryEpoch: 0,

      prepareScope: (scope) => {
        set((state) => {
          const existing = state.entriesByConversationId[scope.conversationId];
          if (existing && sameScope(existing.scope, scope)) {
            return state;
          }
          const recoveryEpoch = state.nextRecoveryEpoch + 1;
          return {
            nextRecoveryEpoch: recoveryEpoch,
            entriesByConversationId: {
              ...state.entriesByConversationId,
              [scope.conversationId]: {
                scope,
                loading: false,
                error: null,
                recoveryEpoch,
                revisionWatermark: 0,
              },
            },
          };
        });
      },

      isCurrentScope: (scope) => {
        const entry = get().entriesByConversationId[scope.conversationId];
        return Boolean(entry && sameScope(entry.scope, scope));
      },

      applyReplacement: (actorId, suggestions, options) => {
        if (suggestions.actorId !== actorId) return false;
        const conversationId = suggestions.conversationId;
        const existing = get().entriesByConversationId[conversationId];
        if (!existing || existing.scope.actorId !== actorId) return false;
        const reduced = reduceSuggestionReplacement(
          existing,
          suggestions,
          options?.etag,
        );
        if (!reduced.applied) return false;
        set((state) => ({
          entriesByConversationId: {
            ...state.entriesByConversationId,
            [conversationId]: reduced.entry,
          },
        }));
        return true;
      },

      ensure: (scope) => load(scope, ensureConversationSuggestions),
      recover: (scope) => load(scope, fetchConversationSuggestions),

      clearConversation: (conversationId) => {
        set((state) => {
          const next = { ...state.entriesByConversationId };
          delete next[conversationId];
          return { entriesByConversationId: next };
        });
      },

      clearAll: () =>
        set((state) => ({
          entriesByConversationId: {},
          nextRecoveryEpoch: state.nextRecoveryEpoch + 1,
        })),
    };
  },
);
